import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Collapse,
  useTheme,
} from "@mui/material";
import ArchivalOptionsForm from "./services/ArchivalOptionsForm";
import DownloadSettingsForm from "./services/DownloadSettingsForm";
import MergingForm from "./services/MergingForm";
import { getFormSectionStyles } from "../formSectionStyles";

const SERVICE_COMPONENTS: Record<string, React.FC<any>> = {
  archivalOptions: ArchivalOptionsForm,
  downloadSettings: DownloadSettingsForm,
  merging: MergingForm,
};

interface AssetStorageFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const SERVICE_OPTIONS = Object.keys(SERVICE_COMPONENTS);

const AssetStorageForm: React.FC<AssetStorageFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const cachedConfigsRef = useRef<Record<string, any>>({});

  const enabledServices = useMemo(() => {
    return SERVICE_OPTIONS.filter(
      (s) => config[`assetStorage_${s}_serviceConfig`]
    );
  }, [config]);

  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetStorage_${service}_serviceConfig`;
    if (enabled) {
      const restored = cachedConfigsRef.current[service] || {};
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      cachedConfigsRef.current[service] = config[serviceKey];
      handleChange(serviceKey, undefined);
    }
  };

  return (
    <Paper
      sx={styles.paper}
    >
      <Typography
        variant="h6"
        sx={styles.title}
      >
        Asset Storage Services
      </Typography>

      <FormGroup sx={{ flexDirection: "row", flexWrap: "wrap", gap: 2 }}>
        {SERVICE_OPTIONS.map((service) => {
          const enabled = enabledServices.includes(service);
          return (
            <FormControlLabel
              key={service}
              control={
                <Checkbox
                  checked={enabled}
                  onChange={(e) => toggleService(service, e.target.checked)}
                  sx={styles.checkbox}
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  {service.charAt(0).toUpperCase() + service.slice(1)}
                </Typography>
              }
            />
          );
        })}
      </FormGroup>

      {SERVICE_OPTIONS.map((service) => {
        const Component = SERVICE_COMPONENTS[service];
        const serviceKey = `assetStorage_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);
        return (
          <Collapse key={service} in={enabled} unmountOnExit>
            <Box
              sx={styles.collapsibleRail}
            >
              <Component
                config={config[serviceKey] || {}}
                onChange={(field: string, value: any) =>
                  handleChange(`${serviceKey}.${field}`, value)
                }
              />
            </Box>
          </Collapse>
        );
      })}
    </Paper>
  );
};

export default AssetStorageForm;
